import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@sluitstraat/core';
import { personenAutoStyles as styles } from '../../../styles/commonStyles';

export const VehicleSummaryCard: React.FC<{ ob: any }> = ({ ob }) => {
  const { t } = useTranslation();

              const hasVehicleData = ob?.OB_MERK || ob?.OB_MODEL;
              if (!hasVehicleData) return null;

              const fuelMap: Record<string, string> = {
                A: t('screens.personenauto.fuelTypes.cng'), B: t('screens.personenauto.fuelTypes.petrol'),
                D: t('screens.personenauto.fuelTypes.diesel'), E: t('screens.personenauto.fuelTypes.electric'),
                G: t('screens.personenauto.fuelTypes.lpg'), H: t('screens.personenauto.fuelTypes.hybrid'),
                I: t('screens.personenauto.fuelTypes.hybridDiesel'), J: t('screens.personenauto.fuelTypes.hybridPetrol'),
                L: t('screens.personenauto.fuelTypes.lng'), W: t('screens.personenauto.fuelTypes.hydrogen'),
                Z: t('screens.personenauto.fuelTypes.other'),
              };
              const transMap: Record<string, string> = {
                A: t('screens.personenauto.transmissionTypes.automatic'), H: t('screens.personenauto.transmissionTypes.manual'),
                S: t('screens.personenauto.transmissionTypes.semiAutomatic'), V: t('screens.personenauto.transmissionTypes.cvt'),
              };
              const monthNames = [
                t('screens.personenauto.months.january'), t('screens.personenauto.months.february'),
                t('screens.personenauto.months.march'), t('screens.personenauto.months.april'),
                t('screens.personenauto.months.may'), t('screens.personenauto.months.june'),
                t('screens.personenauto.months.july'), t('screens.personenauto.months.august'),
                t('screens.personenauto.months.september'), t('screens.personenauto.months.october'),
                t('screens.personenauto.months.november'), t('screens.personenauto.months.december'),
              ];

              // Group 1: Vehicle identity (skip duplicates between brand/model and type/uitvoering)
              const identityGroup = [
                { label: t('screens.personenauto.fields.brand'), value: ob?.OB_MERK || '' },
                ob?.OB_MODEL && ob.OB_MODEL !== ob.OB_MERK ? { label: t('screens.personenauto.fields.model'), value: ob.OB_MODEL } : null,
                ob?.OB_TYPE ? { label: t('screens.personenauto.fields.vehicleType'), value: ob.OB_TYPE } : null,
                ob?.OB_UITVOMS && ob.OB_UITVOMS !== ob.OB_TYPE ? { label: t('screens.personenauto.fields.variant'), value: ob.OB_UITVOMS } : null,
              ].filter((item): item is { label: string; value: string } => item !== null && item.value !== '');

              // Group 2: Build date & production details
              const buildGroup = [
                { label: t('screens.personenauto.fields.buildYear'), value: ob?.OB_BOUWJR ? `${ob.OB_BOUWJR}` : '' },
                ob?.OB_BOUWMND ? { label: t('screens.personenauto.fields.buildMonth'), value: monthNames[(ob.OB_BOUWMND || 1) - 1] || '' } : null,
                ob?.OB_ACTKLR1 ? { label: t('screens.personenauto.fields.color', 'Kleur'), value: ob.OB_ACTKLR1 + (ob?.OB_ACTKLR2 ? ` / ${ob.OB_ACTKLR2}` : '') } : null,
                ob?.OB_CARREST ? { label: t('screens.personenauto.fields.bodyType', 'Carrosserie'), value: ob.OB_CARREST } : null,
                ob?.OB_SEGMNTT ? { label: t('screens.personenauto.fields.segment', 'Segment'), value: ob.OB_SEGMNTT } : null,
              ].filter((item): item is { label: string; value: string } => item !== null && item.value !== '');

              // Group 3: Powertrain
              const powertrainGroup = [
                { label: t('screens.personenauto.fields.fuelType'), value: [fuelMap[ob?.OB_BRANSTO] || ob?.OB_BRANSTO, ob?.OB_BRNDST2 ? (fuelMap[ob.OB_BRNDST2] || ob.OB_BRNDST2) : '', ob?.OB_BRNDST3 ? (fuelMap[ob.OB_BRNDST3] || ob.OB_BRNDST3) : ''].filter(Boolean).join(' / ') },
                { label: t('screens.personenauto.fields.transmission'), value: transMap[ob?.OB_TRSOORT] || ob?.OB_TRSOORT || '' },
                { label: t('screens.personenauto.fields.kilowatt'), value: ob?.OB_AANTKW ? `${formatNumber(ob.OB_AANTKW)} kW` : '' },
                { label: t('screens.personenauto.fields.turbo'), value: ob?.OB_TURBO === 'J' ? t('common.yes') : ob?.OB_TURBO === 'N' ? t('common.no') : '' },
                ob?.OB_ACCELER ? { label: t('screens.personenauto.fields.acceleration'), value: `${ob.OB_ACCELER}s` } : null,
                ob?.OB_MAXSNEL ? { label: t('screens.personenauto.fields.maxSpeed', 'Max. snelheid'), value: `${ob.OB_MAXSNEL} km/h` } : null,
                ob?.OB_ABS ? { label: t('screens.personenauto.fields.abs', 'ABS'), value: ob.OB_ABS === 'J' ? t('common.yes') : ob.OB_ABS === 'N' ? t('common.no') : '' } : null,
                ob?.OB_ELABEL ? { label: t('screens.personenauto.fields.energyLabel', 'Energielabel'), value: ob.OB_ELABEL } : null,
              ].filter((item): item is { label: string; value: string } => item !== null && item.value !== '');

              // Group 4: Value & other
              const otherGroup = [
                { label: t('screens.personenauto.fields.catalogValue'), value: ob?.OB_VWACA ? `\u20AC ${formatNumber(ob.OB_VWACA)}` : '' },
                ob?.OB_ADEUR ? { label: t('screens.personenauto.fields.doors'), value: `${ob.OB_ADEUR}` } : null,
                { label: t('screens.personenauto.fields.imported'), value: ob?.OB_IMPOBJ === 'J' ? t('common.yes') : ob?.OB_IMPOBJ === 'N' ? t('common.no') : '' },
                ob?.OB_MLDCODE ? { label: t('screens.personenauto.fields.meldcode', 'Meldcode'), value: ob.OB_MLDCODE } : null,
              ].filter((item): item is { label: string; value: string } => item !== null && item.value !== '');

              const allGroups: Array<{ title: string; items: Array<{ label: string; value: string }> }> = [
                { title: t('screens.personenauto.fields.groupModelDetails'), items: identityGroup },
                { title: t('screens.personenauto.fields.groupManufactoryDetails'), items: buildGroup },
                { title: t('screens.personenauto.fields.groupPerformance'), items: powertrainGroup },
                { title: t('screens.personenauto.fields.groupValueOther'), items: otherGroup },
              ].filter(g => g.items.length > 0);

              const renderItem = (item: { label: string; value: string }) => (
                <div key={item.label} style={styles.rowBetweenBaseline}>
                  <span style={styles.hint13}>{item.label}</span>
                  <span style={styles.matrixValue}>{item.value}</span>
                </div>
              );

              return (
                <div style={styles.vehicleSummaryCard}>
                  {allGroups.map((group, idx) => (
                    <div key={idx}>
                      {idx > 0 && <div style={styles.divider10} />}
                      <span style={styles.vehicleSummaryBadge}>
                        {group.title}
                      </span>
                      <div className="personenauto-vehicle-grid" style={styles.vehicleSummaryGrid}>
                        {group.items.map(renderItem)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            };
